"use client";

import { dataLayer } from "@repo/analytics/data-layer-utils";
import { getSearchTerm } from "@repo/analytics/functions";
import { componentNameConstants, dlEventConstants } from "@repo/analytics/data-layer.constants";
import BFLIcon from "@repo/shared-ui/BFLIcon";
import { getSessionData } from "@repo/shared-utils/common-utils";

import { selectIsAuthSuccessOrPerpetual, useAppSelector } from "@repo/global-store";
import Image from "next/legacy/image";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { __LIVE_LANDING_PAGE__ } from "../../constants/livelanding-page.constants";
import { LiveStream } from "../../types/upcoming-live.types";
import {
  claimSharedMediaPlayer,
  dismissFireworkPipBeforePlayback,
  formatDurationFull,
  formatScheduledDate,
  isSoftPipOverlayHiddenForEncodedId,
  isEventForPlayer,
  SHARED_PLAYER_ACTIVATE_EVENT,
  timeSinceEnded,
  getSubsectionFromHashtags,
  triggerPlayerFullscreen,
} from "../../utils/mediaUtils";
import { showToast } from "../../utils/toast";
import { thumbnailLoader } from "../../utils/image";
import {
  buildLiveLandingReminderCT,
  buildLiveLandingVideoCardClickCT,
  trackUnifiedMediaCardProductClick,
} from "../../utils/livecommerceCleverTap";
import { getCtSectionTitle } from "../HeadlessPlaylistSection/headlessPlaylistUtils";
import { ShadowSpinner } from "../Loader/ShadowSpinner";
import { PipOverlayLabel } from "../MediaCard/PipOverlayLabel";
import { VideoPlayer } from "../VideoPlayer/VideoPlayer";
import { ReminderService, ToastService } from "./UpcomingLive";
import { useFireworkPlayerEvents } from "../../hooks/useFireworkPlayerEvents";

const LiveSessionCardLiveComponent = ({
  session,
  setReminderText,
  reminderSetText,
  isReminderSet: initialReminderSet,
  onReminderToggle,
  bordernone,
  isFeaturedLiveCard,
  title,
  titleCt,
  reminderService,
  toastService,
  enableDynamicPlayer = false,
}: {
  session: LiveStream;
  setReminderText?: string;
  reminderSetText?: string;
  isReminderSet?: boolean;
  bordernone?: string;
  isFeaturedLiveCard?: boolean;
  onReminderToggle?: (videoId: string, isSet: boolean) => Promise<void>;
  title?: string;
  titleCt?: string;
  influencerId?: string;
  reminderService?: ReminderService;
  toastService?: ToastService;
  enableDynamicPlayer?: boolean;
}) => {
  const isLoggedIn = useAppSelector(selectIsAuthSuccessOrPerpetual);
  const [isReminderSet, setIsReminderSet] = useState(initialReminderSet);
  const [isLoading, setIsLoading] = useState(false);
  const [isPlayerActive, setIsPlayerActive] = useState(false);
  const [isPlayerLoading, setIsPlayerLoading] = useState(false);
  const [pipThumbnailOverlayHidden, setpipThumbnailOverlayHidden] = useState(false);

  const cardRef = useRef<HTMLDivElement>(null);
  const playerLoaderStartedAtRef = useRef<number>(0);
  const playerLoaderCloseTimeoutRef = useRef<number | null>(null);
  const encodedId = session?.trailer?.encoded_id;
  const MIN_PLAYER_LOADER_MS = 1000;
  const reminderInfluencerId = __LIVE_LANDING_PAGE__;
  const ctSectionTitle = getCtSectionTitle(titleCt, title, "Live Sessions");
  const upcomingCtSectionTitle = getCtSectionTitle(titleCt, title, "Upcoming Live");

  const clearPlayerLoaderCloseTimeout = useCallback(() => {
    if (playerLoaderCloseTimeoutRef.current !== null) {
      window.clearTimeout(playerLoaderCloseTimeoutRef.current);
      playerLoaderCloseTimeoutRef.current = null;
    }
  }, []);

  const startPlayerLoader = useCallback(() => {
    if (!enableDynamicPlayer) return;
    clearPlayerLoaderCloseTimeout();
    playerLoaderStartedAtRef.current = Date.now();
    setIsPlayerLoading(true);
  }, [clearPlayerLoaderCloseTimeout, enableDynamicPlayer]);

  const stopPlayerLoader = useCallback(() => {
    clearPlayerLoaderCloseTimeout();
    const elapsed = Date.now() - playerLoaderStartedAtRef.current;
    const remaining = Math.max(0, MIN_PLAYER_LOADER_MS - elapsed);

    if (remaining === 0) {
      setIsPlayerLoading(false);
      return;
    }

    playerLoaderCloseTimeoutRef.current = window.setTimeout(() => {
      setIsPlayerLoading(false);
      playerLoaderCloseTimeoutRef.current = null;
    }, remaining);
  }, [clearPlayerLoaderCloseTimeout]);

  useEffect(() => {
    setIsReminderSet(initialReminderSet);
  }, [initialReminderSet, session?.id]);

  useEffect(() => {
    const syncOverlayFromStorage = () => {
      setpipThumbnailOverlayHidden(isSoftPipOverlayHiddenForEncodedId(encodedId));
    };

    syncOverlayFromStorage();
    if (typeof window === "undefined") return;

    const onStorage = () => {
      syncOverlayFromStorage();
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", syncOverlayFromStorage);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", syncOverlayFromStorage);
    };
  }, [encodedId]);

  // Removed handleBlockbusterHoverEnter and handleBlockbusterHoverLeave to prevent CT events on hover

  const handleBlockbusterClick = useCallback(() => {
    const data = {
      section_title: title || "Live Sessions",
      component: componentNameConstants.UPCOMING_LIVE,
      event: dlEventConstants.THUMBNAIL_CLICK,
      element: dlEventConstants.THUMBNAIL_CLICK,
      cta_text: session?.event_name || "Explore Now",
    };

    const videoId = session?.trailer?.encoded_id || session?.id || "NA";
    const categoryFromVideo =
      (typeof session?.event_description === "string" && session.event_description.trim()) ||
      (typeof session?.event_name === "string" && session.event_name.trim()) ||
      "NA";
    const cleverTapObject = buildLiveLandingVideoCardClickCT({
      section: ctSectionTitle,
      ctaText: session?.event_name || "Explore Now",
      videoId,
      videoTitle: session?.event_name || "NA",
      liveSession: session?.status === "active" ? "ACTIVE" : "INACTIVE",
      productName: categoryFromVideo,
      productCategory: `${categoryFromVideo || "NA"} || ${videoId}`,
      searchTerm: getSearchTerm(),
      subSection: getSubsectionFromHashtags(session?.trailer?.hashtags),
    });

    dataLayer("", data, cleverTapObject);
  }, [ctSectionTitle, session, title]);

  useEffect(() => {
    if (!isFeaturedLiveCard || !cardRef.current) return;
    const card = cardRef.current;
    card.addEventListener("click", handleBlockbusterClick);
    // Removed mouseenter/mouseleave event listeners to prevent CT events on hover
    return () => {
      card.removeEventListener("click", handleBlockbusterClick);
      // Removed mouseenter/mouseleave event listeners to prevent CT events on hover
    };
  }, [isFeaturedLiveCard, handleBlockbusterClick]);

  const shouldHandlePlayerEvent = useCallback(
    (event: Event) => {
      const detail = (event as CustomEvent).detail;
      return isEventForPlayer(detail, encodedId);
    },
    [encodedId]
  );

  const handlePlayerTransition = useCallback(
    (event: Event) => {
      if (!shouldHandlePlayerEvent(event)) return;
      const detail = (event as CustomEvent).detail;
      const transitionTo = detail?.transitionTo ?? detail?.to ?? detail?.state;
      const normalizedTransition =
        typeof transitionTo === "string" ? transitionTo.trim().toLowerCase() : "";

      if (normalizedTransition === "minimized" || normalizedTransition === "minimized state") {
        setpipThumbnailOverlayHidden(true);
      } else if (normalizedTransition) {
        setpipThumbnailOverlayHidden(false);
      }

      console.log("[FW][LiveLandingPageWrapper] fw:player:transition", {
        eventType: event.type,
        transitionTo,
        detail,
        encodedId,
      });
      if (!transitionTo || transitionTo !== "opening") {
        stopPlayerLoader();
      }
    },
    [encodedId, shouldHandlePlayerEvent, stopPlayerLoader]
  );

  const handlePlayerQuit = useCallback(
    (event: Event) => {
      if (!shouldHandlePlayerEvent(event)) return;
      console.log("[FW][LiveLandingPageWrapper] quit event fired", {
        eventType: event.type,
        encodedId,
      });
      setpipThumbnailOverlayHidden(false);
      setIsPlayerActive(false);
      stopPlayerLoader();
    },
    [encodedId, shouldHandlePlayerEvent, stopPlayerLoader]
  );

  useFireworkPlayerEvents({
    enabled: Boolean(isFeaturedLiveCard && enableDynamicPlayer),
    onTransition: handlePlayerTransition,
    onQuit: handlePlayerQuit,
  });

  useEffect(() => {
    if (!encodedId || !enableDynamicPlayer) return;

    const handleSharedActivate = (event: Event) => {
      const detail = (event as CustomEvent<{ encodedId?: string }>).detail;
      const nextEncodedId = detail?.encodedId;
      if (!nextEncodedId || nextEncodedId === encodedId) return;
      setpipThumbnailOverlayHidden(false);
      setIsPlayerActive(false);
      stopPlayerLoader();
    };

    document.addEventListener(SHARED_PLAYER_ACTIVATE_EVENT, handleSharedActivate);
    return () => {
      document.removeEventListener(SHARED_PLAYER_ACTIVATE_EVENT, handleSharedActivate);
    };
  }, [encodedId, enableDynamicPlayer, stopPlayerLoader]);

  useEffect(() => {
    return () => {
      clearPlayerLoaderCloseTimeout();
    };
  }, [clearPlayerLoaderCloseTimeout]);

  const handleDynamicPlayerOpen = useCallback(() => {
    if (!encodedId) return;

    if (isPlayerActive) {
      claimSharedMediaPlayer(encodedId);
      startPlayerLoader();
      triggerPlayerFullscreen(`player-${encodedId}`);
      return;
    }

    dismissFireworkPipBeforePlayback();
    claimSharedMediaPlayer(encodedId);
    startPlayerLoader();
    setIsPlayerActive(true);
    triggerPlayerFullscreen(`player-${encodedId}`);
  }, [encodedId, isPlayerActive, startPlayerLoader]);

  const handleCardClick = useCallback(() => {
    if (!isFeaturedLiveCard || !enableDynamicPlayer) return;
    handleDynamicPlayerOpen();
  }, [isFeaturedLiveCard, enableDynamicPlayer, handleDynamicPlayerOpen]);

  useEffect(() => {
    if (!isFeaturedLiveCard || !encodedId || !isPlayerActive) return;

    const handleFwProductClick = (event: Event) => {
      trackUnifiedMediaCardProductClick({
        event,
        encodedId,
        fallbackVideoDescription:
          session?.event_description || session?.trailer?.description || "NA",
      });
    };

    document.addEventListener("fw:shopping:product-clicked", handleFwProductClick);

    return () => {
      document.removeEventListener("fw:shopping:product-clicked", handleFwProductClick);
    };
  }, [
    encodedId,
    isFeaturedLiveCard,
    isPlayerActive,
    session?.event_description,
    session?.trailer?.description,
  ]);

  const handleCardKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!isFeaturedLiveCard || !enableDynamicPlayer) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleDynamicPlayerOpen();
      }
    },
    [isFeaturedLiveCard, enableDynamicPlayer, handleDynamicPlayerOpen]
  );

  const handleSetReminder = async () => {
    if (!isLoggedIn) {
      return;
    }

    try {
      setIsLoading(true);

      const sessionData = await getSessionData();
      const mobileNo = sessionData?.mobileNo;

      if (!mobileNo) {
        throw new Error("Mobile number not found. Please login again.");
      }

      if (!session.id) {
        throw new Error("Session ID not found.");
      }

      if (isReminderSet) {
        const data = {
          section_title: title || "Upcoming Live",
          component: componentNameConstants.UPCOMING_LIVE,
          event: dlEventConstants.CLICK,
          element: "REMINDER_REMOVED",
          cta_text: reminderSetText || "REMINDER SET",
        };

        const cleverTapObject = buildLiveLandingReminderCT({
          videoId: session?.id || "NA",
          videoTitle: session?.event_name || "NA",
          cta: "REMINDER_REMOVED",
          section: upcomingCtSectionTitle,
          creatorName: session?.trailer?.creator?.name || "NA",
        });

        dataLayer("", data, cleverTapObject);

        const response = await reminderService?.removeReminder({
          mobileNo,
          videoId: session.id,
          influencerId: reminderInfluencerId,
        });

        if (response?.data?.deleted) {
          setIsReminderSet(false);
          showToast({
            variant: "success",
            message: "Reminder removed successfully",
          });
          await onReminderToggle?.(session.id, false);
        } else {
          throw new Error(response?.message || "Failed to remove reminder");
        }
      } else {
        const data = {
          section_title: title || "Upcoming Live",
          component: componentNameConstants.UPCOMING_LIVE,
          event: dlEventConstants.CLICK,
          element: "SET_REMINDER",
          cta_text: setReminderText || "SET REMINDER",
        };

        const cleverTapObject = buildLiveLandingReminderCT({
          videoId: session?.id || "NA",
          videoTitle: session?.event_name || "NA",
          cta: "SET_REMINDER",
          section: upcomingCtSectionTitle,
          creatorName: session?.trailer?.creator?.name || "NA",
        });

        dataLayer("", data, cleverTapObject);

        const response = await reminderService?.setReminder({
          mobileNo,
          influencerId: reminderInfluencerId,
          videoId: session.id,
        });

        if (response?.data?.reminder) {
          setIsReminderSet(true);
          showToast({
            variant: "success",
            message: "Reminder added successfully",
          });
          await onReminderToggle?.(session.id, true);
        } else {
          throw new Error(response?.message || "Failed to set reminder");
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to update reminder";
      toastService?.showToast({
        variant: "error",
        message: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const thumbnail =
    session.video_posters?.find((poster) => poster.orientation === "horizontal")?.url ||
    session.video_posters?.find((poster) => poster.video_poster_type === "static")?.url ||
    session.trailer?.thumbnail_url ||
    "https://via.placeholder.com/300x533";

  const isInteractiveCard = !!(isFeaturedLiveCard && enableDynamicPlayer);

  // Hidden behind a conditional spread so jsx-a11y/Sonar don't see `tabIndex`
  // sitting on a non-interactive <div>; when interactive, role + tabIndex are
  // both present and consistent.
  const interactiveCardProps = isInteractiveCard
    ? {
        role: "button" as const,
        tabIndex: 0,
        onClick: handleCardClick,
        onKeyDown: handleCardKeyDown,
      }
    : undefined;

  const renderReminderLabel = () => {
    if (isLoading) return <span className="text-[12px]">Loading...</span>;
    if (isReminderSet) {
      return (
        <>
          <BFLIcon
            size="12px"
            className="icomoon transform text-[12px] text-[#00B500] transition-transform duration-300 before:content-['\e91f']"
          />
          <span className="text-[12px]">{reminderSetText}</span>
        </>
      );
    }
    return (
      <>
        <BFLIcon
          size="12px"
          className="icomoon transform text-[12px] transition-transform duration-300 before:content-['\e932']"
        />
        <span className="text-[12px]">{setReminderText}</span>
      </>
    );
  };

  const renderThumbnail = () => {
    if (!isFeaturedLiveCard) {
      return (
        <Image
          src={thumbnail}
          alt={session.event_name || ""}
          className="object-cover"
          layout="fill"
          unoptimized={true}
          priority={false}
          quality={100}
        />
      );
    }
    return (
      <>
        <div className="absolute inset-0 top-0 left-0 h-full w-full">
          {/* <video
            className="absolute top-0 left-0 h-full w-full object-cover"
            src={session.trailer.file_url}
            loop
            playsInline
            aria-hidden="true"
            tabIndex={-1}
          >
            <track kind="captions" />
          </video> */}
          {(!enableDynamicPlayer || isPlayerActive) && session.trailer?.encoded_id && (
            <VideoPlayer
              className="h-full w-full"
              videoId={session.trailer?.encoded_id}
              autoplay={false}
              maxVideos={1}
              playerCaptions={true}
              productCard={false}
              enableTracking
              sectionTitle={title || "Live Sessions"}
              eventName={session.event_name}
            />
          )}
        </div>

        <div className="absolute inset-0 bg-black/20">
          <Image
            src={thumbnail}
            alt={session.event_name || "Video"}
            className="h-full w-full object-cover"
            layout="fill"
            loader={thumbnailLoader}
            quality={70}
            priority={false}
          />
        </div>

        {pipThumbnailOverlayHidden && <PipOverlayLabel />}

        {enableDynamicPlayer && isPlayerLoading && (
          <div className="absolute inset-0 z-[30] flex items-center justify-center bg-black/50">
            <ShadowSpinner ariaLabel="Loading video" />
          </div>
        )}
      </>
    );
  };

  const renderActionCell = () => {
    if (isFeaturedLiveCard) {
      return (
        <div className="absolute right-0 bottom-0 z-[2] flex rounded-[2px] bg-[#1A1A1AB2] p-0 py-1 leading-[1.2] tracking-[0px]">
          <span className="font-rubik px-1 text-center !text-[10px] font-medium text-[#FFFFFF] md:!text-[14px]">
            {formatDurationFull(session.duration ?? 0)}
          </span>
        </div>
      );
    }
    if (!isLoggedIn) return null;
    const reminderAriaLabel = isReminderSet
      ? `Remove reminder for ${session.event_name || "upcoming live session"}`
      : `Set reminder for ${session.event_name || "upcoming live session"}`;
    return (
      <button
        type="button"
        onClick={handleSetReminder}
        disabled={isLoading}
        aria-label={reminderAriaLabel}
        className="flex flex-1 items-center justify-center gap-[4px] rounded-full bg-white px-[8px] py-[4px] font-semibold text-black transition-all duration-300 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {renderReminderLabel()}
      </button>
    );
  };

  const renderBlockbusterMeta = () => {
    if (!isFeaturedLiveCard) return null;
    return (
      <div className="!w-[343px] max-[370px]:!w-[314px] max-[324px]:!w-[290px]">
        {session?.event_name && (
          <h3 className="line-clamp-2 overflow-hidden pt-[6px] font-[Rubik] text-[16px] leading-[1.4] font-normal text-[#1A1A1A] max-[1200px]:text-[16px] max-[768px]:text-[14px] max-[420px]:text-[12px]">
            {session.event_name}
          </h3>
        )}

        {session?.event_description && (
          <div className="-ml-[2px] inline-block px-[2px] py-[2px] font-[Rubik] text-[14px] font-normal text-[#999999] max-[1200px]:text-[14px] max-[768px]:text-[14px] max-[420px]:text-[12px]">
            {session.event_description}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div
        ref={isFeaturedLiveCard ? cardRef : undefined}
        {...interactiveCardProps}
        className={`group relative overflow-hidden rounded-[8px] border border-gray-800 bg-gray-900 transition-all duration-300 hover:border-gray-700 sm:max-w-[343px] md:max-w-[343px] ${isInteractiveCard ? "cursor-pointer" : ""} ${bordernone}`}
      >
        <div className="h-[200px]">
          {renderThumbnail()}
          {!isFeaturedLiveCard && (
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
          )}

          <div className="absolute top-[10px] left-[10px] rounded-[2px] bg-[rgba(26,26,26,0.7)] px-[4px] py-[2px] backdrop-blur-sm">
            <p
              className={`font-[500] text-white ${isFeaturedLiveCard ? "!text-[10px] md:!text-[14px]" : "text-[10px]"}`}
            >
              {isFeaturedLiveCard
                ? timeSinceEnded(session?.ended_at)
                : formatScheduledDate(session?.scheduled_at_formatted)}
            </p>
          </div>
        </div>

        <div className="absolute right-[10px] bottom-[10px] left-[10px] flex flex-row items-end justify-between gap-[8px]">
          {!isFeaturedLiveCard && (
            <div className="flex-2">
              <h3 className="text-[14px] font-[500] text-white">
                {session?.trailer?.creator?.name}
              </h3>
              <h2 className="max-w-[220px] text-[12px] leading-tight font-[400] text-white">
                {session.event_name}
              </h2>
              {session?.event_description && (
                <p className="text-[10px] text-[#CCCCCC] text-[400]">{session.event_description}</p>
              )}
            </div>
          )}
          {renderActionCell()}
        </div>
      </div>
      {renderBlockbusterMeta()}
    </>
  );
};

export const LiveSessionCardLive = memo(LiveSessionCardLiveComponent);
